import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import AxisSpinner from "./AxisSpinner";
import { BsArrowClockwise, BsBoxArrowUpRight } from "react-icons/bs";

export const StatEmailLatency = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [latencyData, setLatencyData] = useState([]);
  const [errorEmails, setErrorEmails] = useState([]);
  const [chartData, setChartData] = useState(null);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const [startDate, setStartDate] = useState(
    thirtyDaysAgo.toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (latencyData.length > 0) {
      buildChartData();
    } else {
      setChartData(null);
    }
  }, [latencyData]);

  const buildChartData = () => {
    const sorted = [...latencyData].sort(
      (a, b) => new Date(a.senderDate) - new Date(b.senderDate),
    );
    setChartData({
      labels: sorted.map((d) => d.senderDate),
      datasets: [
        {
          label: "Latency (seconds)",
          data: sorted.map((d) => d.latencySeconds),
          borderColor: "#00005F",
          backgroundColor: "rgba(0, 0, 95, 0.08)",
          tension: 0.3,
          pointRadius: 3,
          fill: true,
        },
      ],
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const start = new Date(startDate + "T00:00:00").toISOString();
      const end = new Date(endDate + "T23:59:59").toISOString();

      const config = {
        headers: { "content-type": "application/json" },
        params: { start, end },
      };

      const [latencyRes, errorsRes] = await Promise.all([
        authAxios().get("/api/backoffice/reporting/postmark_latency", config),
        authAxios().get(
          "/api/backoffice/reporting/postmark_error_emails",
          config,
        ),
      ]);

      setLatencyData(latencyRes.data || []);
      setErrorEmails(errorsRes.data || []);
    } catch (e) {
      console.log("Error fetching email latency data");
      console.log(e);
    }
    setLoading(false);
  };

  const MAX_DAYS = 30;

  const clampEnd = (start, end) => {
    const maxEnd = new Date(start);
    maxEnd.setDate(maxEnd.getDate() + MAX_DAYS);
    return new Date(end) > maxEnd ? maxEnd.toISOString().split("T")[0] : end;
  };

  const handleStartChange = (newStart) => {
    setStartDate(newStart);
    setEndDate((prev) => clampEnd(newStart, prev));
  };

  const handleEndChange = (newEnd) => {
    setEndDate(clampEnd(startDate, newEnd));
  };

  const getPostmarkId = (url) => {
    const parts = url.split("/");
    return parts[parts.length - 1];
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          font: { size: 13, weight: "bold" },
          color: "#00005F",
        },
      },
      tooltip: {
        backgroundColor: "#00005F",
        borderColor: "#EEEFEF",
        borderWidth: 2,
        titleFont: { size: 16 },
        bodyFont: { size: 13, weight: "bold" },
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y.toFixed(2)}s`,
        },
      },
    },
    scales: {
      x: {
        ticks: { maxTicksLimit: 20, maxRotation: 45, font: { size: 11 } },
      },
      y: {
        title: { display: true, text: "Latency (seconds)", font: { size: 12 } },
        beginAtZero: true,
      },
    },
  };

  return (
    <Fragment>
      <Row className="mb-3 align-items-end">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">
              Start Date
            </Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => handleStartChange(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => handleEndChange(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Button
            variant="outline-secondary"
            onClick={fetchData}
            disabled={loading}
          >
            <BsArrowClockwise className="me-1" /> Fetch
          </Button>
        </Col>
        <Form.Text className="text-muted">Max range: 30 days</Form.Text>
      </Row>

      {loading ? (
        <AxisSpinner loading={loading} />
      ) : (
        <Fragment>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Email Processing Latency</h6>
              <Badge bg="secondary">{latencyData.length} records</Badge>
            </Card.Header>
            <Card.Body>
              {chartData ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center py-4 mb-0">
                  No latency data for this date range.
                </p>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Error Emails</h6>
              <Badge bg={errorEmails.length > 0 ? "danger" : "success"}>
                {errorEmails.length}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {errorEmails.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0">
                  No error emails for this date range.
                </p>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Message ID</th>
                      <th>Postmark Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorEmails.map((email, i) => {
                      const id = getPostmarkId(email.postmarkUrl);
                      return (
                        <tr key={i}>
                          <td className="text-muted">{i + 1}</td>
                          <td>
                            <code>{id}</code>
                          </td>
                          <td>
                            <a
                              href={email.postmarkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View in Postmark{" "}
                              <BsBoxArrowUpRight className="ms-1" />
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Fragment>
      )}
    </Fragment>
  );
};
